import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tagService } from '../../../services/tagService';
import { locationService } from '../../../services/locationService';
import { routeService } from '../../../services/routeService';
import './AdminCategories.css';

const AdminTagAdd = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    color: 'Синий',
    assignedLocations: [],
    assignedRoutes: []
  });
  
  const [availableItems, setAvailableItems] = useState({
    locations: [],
    routes: []
  });
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  
  const [assignedLocationIds, setAssignedLocationIds] = useState(new Set());
  const [assignedRouteIds, setAssignedRouteIds] = useState(new Set());

  const colorOptions = [
    { value: 'Синий', label: 'Синий', colorCode: '#2196F3' },
    { value: 'Красный', label: 'Красный', colorCode: '#F44336' },
    { value: 'Желтый', label: 'Желтый', colorCode: '#FFC107' }
  ];

  useEffect(() => {
    setAssignedLocationIds(new Set(formData.assignedLocations.map(item => item.id)));
  }, [formData.assignedLocations]);

  useEffect(() => {
    setAssignedRouteIds(new Set(formData.assignedRoutes.map(item => item.id)));
  }, [formData.assignedRoutes]);

  useEffect(() => {
    fetchAvailableItems();
  }, []);

  const fetchAvailableItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const [locations, routes] = await Promise.all([
        locationService.getAllLocations(),
        routeService.getAllRoutes()
      ]);
      
      setAvailableItems({
        locations: Array.isArray(locations) ? locations : [],
        routes: Array.isArray(routes) ? routes : []
      });
    } catch (error) {
      console.error('Ошибка загрузки элементов:', error);
      setError('Не удалось загрузить список локаций и маршрутов');
      setAvailableItems({
        locations: [],
        routes: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddLocation = (item) => {
    setFormData(prev => ({
      ...prev,
      assignedLocations: [...prev.assignedLocations, item]
    }));
  };

  const handleRemoveLocation = (itemId) => {
    setFormData(prev => ({
      ...prev,
      assignedLocations: prev.assignedLocations.filter(item => item.id !== itemId)
    }));
  };

  const handleAddRoute = (item) => {
    setFormData(prev => ({
      ...prev,
      assignedRoutes: [...prev.assignedRoutes, item]
    }));
  };

  const handleRemoveRoute = (itemId) => {
    setFormData(prev => ({
      ...prev,
      assignedRoutes: prev.assignedRoutes.filter(item => item.id !== itemId)
    }));
  };

  const getUnassignedLocations = () => {
    const locations = availableItems.locations || [];
    return locations.filter(item => !assignedLocationIds.has(item.id));
  };

  const getUnassignedRoutes = () => {
    const routes = availableItems.routes || [];
    return routes.filter(item => !assignedRouteIds.has(item.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Пожалуйста, введите название тега');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      console.log('1. Отправляем данные для создания тега:', {
        name: formData.name.trim(),
        color: formData.color,
        assignedLocations: formData.assignedLocations.length,
        assignedRoutes: formData.assignedRoutes.length
      });
      
      // 1. Создаем тег (возвращается Tag entity напрямую)
      const newTag = await tagService.createTag(
        formData.name.trim(),
        formData.color
      );
      
      console.log('2. Созданный тег:', newTag);
      
      const tagId = newTag.id;
      
      if (!tagId) {
        throw new Error('Не удалось получить ID созданного тега');
      }
      
      // 2. Привязываем локации к тегу
      if (formData.assignedLocations.length > 0) {
        console.log(`3. Привязываем ${formData.assignedLocations.length} локаций к тегу ${tagId}`);
        const locationIds = formData.assignedLocations.map(item => item.id);
        await tagService.updateTagLocations(tagId, locationIds);
        console.log('4. Локации успешно привязаны');
      }
      
      // 3. Привязываем маршруты к тегу
      if (formData.assignedRoutes.length > 0) {
        console.log(`5. Привязываем ${formData.assignedRoutes.length} маршрутов к тегу ${tagId}`);
        const routeIds = formData.assignedRoutes.map(item => item.id);
        await tagService.updateTagRoutes(tagId, routeIds);
        console.log('6. Маршруты успешно привязаны');
      }
      
      alert('Тег успешно создан');
      navigate('/admin/tags/list');
      
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setError(error.message || 'Не удалось создать тег');
    } finally {
      setSubmitting(false);
    }
  };

  const assignedLocations = formData.assignedLocations || [];
  const assignedRoutes = formData.assignedRoutes || [];
  const unassignedLocations = getUnassignedLocations();
  const unassignedRoutes = getUnassignedRoutes();

  if (loading && !availableItems.locations.length && !availableItems.routes.length) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Загрузка данных...</p>
      </div>
    );
  }

  return (
    <div className="admin-category-form">
      <h2 className="admin-category-form__title">Добавление тега</h2>

      {error && (
        <div className="admin-category-form__error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-category-form__form">
        {/* Основная информация */}
        <div className="admin-category-form__section">
          <h3 className="admin-category-form__section-title">Основная информация</h3>
          
          <div className="admin-category-form__field">
            <label htmlFor="name">
              Название тега <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={submitting}
              placeholder="Введите название тега"
              autoFocus
            />
          </div>

          <div className="admin-category-form__field">
            <label htmlFor="color">Цвет тега <span className="required">*</span></label>
            <div className="admin-category-form__color-selector">
              {colorOptions.map(option => (
                <label 
                  key={option.value} 
                  className={`color-option ${formData.color === option.value ? 'selected' : ''}`}
                  style={{ backgroundColor: option.colorCode }}
                >
                  <input
                    type="radio"
                    name="color"
                    value={option.value}
                    checked={formData.color === option.value}
                    onChange={handleInputChange}
                    disabled={submitting}
                  />
                  <span className="color-label">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Управление локациями */}
        <div className="admin-category-form__section">
          <h3 className="admin-category-form__section-title">
            Управление локациями
          </h3>
          
          <div className="admin-category-form__items-management">
            {/* Левый список: локации в теге */}
            <div className="items-list items-list--assigned">
              <div className="items-list__header">
                <h4>
                  Локации в теге
                  <span className="items-count">({assignedLocations.length})</span>
                </h4>
              </div>
              <div className="items-list__content">
                {assignedLocations.length === 0 ? (
                  <div className="items-list__empty">
                    <span className="empty-icon">📭</span>
                    <p>Нет добавленных локаций</p>
                  </div>
                ) : (
                  <div className="items-grid">
                    {assignedLocations.map(item => (
                      <div key={item.id} className="item-card">
                        <div className="item-card__info">
                          <span className="item-card__name">{item.name}</span>
                          {item.description && (
                            <span className="item-card__description">{item.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveLocation(item.id)}
                          className="item-card__remove"
                          disabled={submitting}
                          title="Удалить из тега"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Правый список: доступные локации */}
            <div className="items-list items-list--available">
              <div className="items-list__header">
                <h4>
                  Доступные локации
                  <span className="items-count">({unassignedLocations.length})</span>
                </h4>
              </div>
              <div className="items-list__content">
                {unassignedLocations.length === 0 ? (
                  <div className="items-list__empty">
                    <span className="empty-icon">✅</span>
                    <p>Все локации уже добавлены</p>
                  </div>
                ) : (
                  <div className="items-grid">
                    {unassignedLocations.map(item => (
                      <div key={item.id} className="item-card item-card--available">
                        <div className="item-card__info">
                          <span className="item-card__name">{item.name}</span>
                          {item.description && (
                            <span className="item-card__description">{item.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddLocation(item)}
                          className="item-card__add"
                          disabled={submitting}
                          title="Добавить в тег"
                        >
                          +
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Управление маршрутами */}
        <div className="admin-category-form__section">
          <h3 className="admin-category-form__section-title">
            Управление маршрутами
          </h3>
          
          <div className="admin-category-form__items-management">
            {/* Левый список: маршруты в теге */}
            <div className="items-list items-list--assigned">
              <div className="items-list__header">
                <h4>
                  Маршруты в теге
                  <span className="items-count">({assignedRoutes.length})</span>
                </h4>
              </div>
              <div className="items-list__content">
                {assignedRoutes.length === 0 ? (
                  <div className="items-list__empty">
                    <span className="empty-icon">📭</span>
                    <p>Нет добавленных маршрутов</p>
                  </div>
                ) : (
                  <div className="items-grid">
                    {assignedRoutes.map(item => (
                      <div key={item.id} className="item-card">
                        <div className="item-card__info">
                          <span className="item-card__name">{item.name}</span>
                          {item.description && (
                            <span className="item-card__description">{item.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveRoute(item.id)}
                          className="item-card__remove"
                          disabled={submitting}
                          title="Удалить из тега"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Правый список: доступные маршруты */}
            <div className="items-list items-list--available">
              <div className="items-list__header">
                <h4>
                  Доступные маршруты
                  <span className="items-count">({unassignedRoutes.length})</span>
                </h4>
              </div>
              <div className="items-list__content">
                {unassignedRoutes.length === 0 ? (
                  <div className="items-list__empty">
                    <span className="empty-icon">✅</span>
                    <p>Все маршруты уже добавлены</p>
                  </div>
                ) : (
                  <div className="items-grid">
                    {unassignedRoutes.map(item => (
                      <div key={item.id} className="item-card item-card--available">
                        <div className="item-card__info">
                          <span className="item-card__name">{item.name}</span>
                          {item.description && (
                            <span className="item-card__description">{item.description}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddRoute(item)}
                          className="item-card__add"
                          disabled={submitting}
                          title="Добавить в тег"
                        >
                          +
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="admin-category-form__actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting || !formData.name.trim()}
          >
            {submitting ? (
              <>
                <span className="btn-spinner"></span>
                Сохранение...
              </>
            ) : (
              'Сохранить тег'
            )}
          </button>
          <button 
            type="button" 
            onClick={() => navigate('/admin/tags/list')} 
            className="btn btn-secondary"
            disabled={submitting}
          >
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminTagAdd;